import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../../../services/api';
import { showSuccessMessage } from '../../../../utils/toast';

const BANK_NAMES = [
  "HDFC Bank",
  "State Bank of India (SBI)",
  "ICICI Bank",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Bank of Baroda",
  "Punjab National Bank",
  "Canara Bank",
  "Union Bank of India",
  "IndusInd Bank"
];

export const PaymentModal = ({ show, onClose, currentOrder, grandTotal, onPaymentComplete }) => {
  // Payment Form States
  const [paymentMethod, setPaymentMethod] = useState('cheque');
  const [payAmount, setPayAmount] = useState('');

  // Cheque fields
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [issuingBank, setIssuingBank] = useState('');

  // UPI fields
  const [upiRefNo, setUpiRefNo] = useState('');
  const [upiPhone, setUpiPhone] = useState('');
  const [upiRemarks, setUpiRemarks] = useState('');

  // Cash fields
  const [cashReceiverName, setCashReceiverName] = useState('');
  const [cashReceiverPhone, setCashReceiverPhone] = useState('');
  const [cashInternalRemarks, setCashInternalRemarks] = useState('');

  // Bank Transfer fields
  const [bankRefId, setBankRefId] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankBranch, setBankBranch] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');
  const [internalRemarks, setInternalRemarks] = useState('');

  const queryClient = useQueryClient();

  const recordPaymentMutation = useMutation({
    mutationFn: async () => {
      let methodDisplay = "Credit / Debit Card";
      let paymentData = {
        amount: Number(payAmount) || 0,
        paymentMethod: paymentMethod,
      };

      if (paymentMethod === 'cheque') {
        methodDisplay = "Cheque";
        paymentData.chequeNumber = chequeNumber;
        paymentData.chequeDate = chequeDate;
        paymentData.bankName = issuingBank;
      } else if (paymentMethod === 'upi') {
        methodDisplay = `UPI`;
        paymentData.upiRefNo = upiRefNo;
        paymentData.upiPhone = upiPhone;
        paymentData.remarks = upiRemarks;
      } else if (paymentMethod === 'cash') {
        methodDisplay = "Cash";
        paymentData.receiverName = cashReceiverName;
        paymentData.receiverPhone = cashReceiverPhone;
        paymentData.remarks = cashInternalRemarks;
      } else if (paymentMethod === 'bank') {
        methodDisplay = `Bank Transfer (${bankName})`;
        paymentData.transactionId = bankRefId;
        paymentData.bankName = bankName;
        paymentData.branch = bankBranch;
        paymentData.ifsc = bankIfsc;
        paymentData.remarks = internalRemarks;
      }
      
      const customerId = currentOrder?.customer_id?._id || currentOrder?.customer_id || currentOrder?._id;
      
      // Save payment to the recieved amounts collection
      await api.post(`/accounts/recieved_amounts`, {
        estimation_id: currentOrder?.order_id ? currentOrder._id : (currentOrder?.estimation_id ? currentOrder.estimation_id : currentOrder?._id),
        customer_id: customerId,
        date: new Date().toISOString(),
        amount: paymentData.amount,
        paymentMethod: paymentData.paymentMethod,
        ...paymentData
      });

      // Optionally update status if it's an order
      if (currentOrder && currentOrder.status === 'ORDER_PLACED') {
        await api.put(`/estimations/${currentOrder._id}/status`, { 
          status: 'ORDER_PLACED', 
          approval_notes: `Advance payment of ₹${Number(payAmount || grandTotal).toLocaleString('en-IN')} received via ${methodDisplay}`,
        }).catch(err => console.error(err));
      }
      
      return customerId;
    },
    onSuccess: (customerId) => {
      onClose();
      showSuccessMessage(`Advance payment recorded successfully!`);
      if (customerId) {
        queryClient.invalidateQueries({ queryKey: ['recieved-amounts', customerId] });
      }
      if (onPaymentComplete) onPaymentComplete();
    }
  });

  const handleConfirmPayment = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    recordPaymentMutation.mutate();
  };

  if (!show) return null;

  const orderIdStr = currentOrder?.order_id || (currentOrder?._id ? `SO-${currentOrder._id.substring(currentOrder._id.length - 6).toUpperCase()}` : 'Order');
  const customerName = currentOrder?.customer_id?.name || currentOrder?.customer_id?.customerName || currentOrder?.customerName || 'Customer';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-2xl w-full p-6 relative">
        <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-5">
          <div>
            <h3 className="text-lg font-bold text-slate-900 tracking-tight">Record Advance Payment</h3>
            <p className="text-xs text-slate-500 font-medium mt-0.5">Order: {orderIdStr} • {customerName}</p>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center text-sm font-bold transition-all"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleConfirmPayment} className="space-y-5">
          {/* Payable Amount Input */}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Payable Amount (₹) (Total: ₹{Number(grandTotal || 0).toLocaleString('en-IN')})</label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-slate-400 font-semibold text-sm">₹</span>
              <input
                type="number"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                className="w-full h-10 pl-8 pr-3 rounded-lg border border-slate-300 text-slate-900 text-sm font-bold outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                placeholder="Enter amount"
                required
              />
            </div>
          </div>

          {/* Payment Method Selector Tabs */}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-2">Select Payment Method</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <button
                type="button"
                onClick={() => setPaymentMethod('cheque')}
                className={`py-2 px-2.5 rounded-lg text-xs font-bold border transition-all text-center ${paymentMethod === 'cheque' ? 'bg-[#0f172a] text-white border-[#0f172a] shadow-xs' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
              >
                🧾 Cheque
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('upi')}
                className={`py-2 px-2.5 rounded-lg text-xs font-bold border transition-all text-center ${paymentMethod === 'upi' ? 'bg-[#0f172a] text-white border-[#0f172a] shadow-xs' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
              >
                📱 UPI
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('cash')}
                className={`py-2 px-2.5 rounded-lg text-xs font-bold border transition-all text-center ${paymentMethod === 'cash' ? 'bg-[#0f172a] text-white border-[#0f172a] shadow-xs' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
              >
                💵 Cash
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('bank')}
                className={`py-2 px-2.5 rounded-lg text-xs font-bold border transition-all text-center ${paymentMethod === 'bank' ? 'bg-[#0f172a] text-white border-[#0f172a] shadow-xs' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
              >
                🏦 Bank
              </button>
            </div>
          </div>

          {/* Dynamic Payment Method Fields */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80 space-y-3">
            {paymentMethod === 'cheque' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Cheque Number <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={chequeNumber}
                    onChange={(e) => setChequeNumber(e.target.value)}
                    placeholder="6-digit No."
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Cheque Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={chequeDate}
                    onChange={(e) => setChequeDate(e.target.value)}
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Issuing Bank <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={issuingBank}
                    onChange={(e) => setIssuingBank(e.target.value)}
                    placeholder="Bank name"
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                    required
                  />
                </div>
              </div>
            )}

            {paymentMethod === 'upi' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">UPI UTR / Ref No. <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={upiRefNo}
                    onChange={(e) => setUpiRefNo(e.target.value)}
                    placeholder="TRX-123..."
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">UPI Phone Number <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    value={upiPhone}
                    onChange={(e) => setUpiPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit number"
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Internal Remarks</label>
                  <input
                    type="text"
                    value={upiRemarks}
                    onChange={(e) => setUpiRemarks(e.target.value)}
                    placeholder="Internal Remarks"
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                  />
                </div>
              </div>
            )}

            {paymentMethod === 'cash' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Receiver Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={cashReceiverName}
                    onChange={(e) => setCashReceiverName(e.target.value)}
                    placeholder="Who received"
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Receiver Phone <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    value={cashReceiverPhone}
                    onChange={(e) => setCashReceiverPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit number"
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Internal Remarks</label>
                  <input
                    type="text"
                    value={cashInternalRemarks}
                    onChange={(e) => setCashInternalRemarks(e.target.value)}
                    placeholder="Internal Remarks"
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                  />
                </div>
              </div>
            )}

            {paymentMethod === 'bank' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Transaction / Ref ID</label>
                  <input
                    type="text"
                    value={bankRefId}
                    onChange={(e) => setBankRefId(e.target.value)}
                    placeholder="TRX-123..."
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Bank Name</label>
                  <input
                    list="bank-names"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="Select or enter bank name"
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500 cursor-pointer"
                    required
                  />
                  <datalist id="bank-names">
                    {BANK_NAMES.map((bank) => (
                      <option key={bank} value={bank} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Branch</label>
                  <input
                    type="text"
                    value={bankBranch}
                    onChange={(e) => setBankBranch(e.target.value)}
                    placeholder="Branch Name"
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">IFSC Code</label>
                  <input
                    type="text"
                    value={bankIfsc}
                    onChange={(e) => setBankIfsc(e.target.value)}
                    placeholder="SBIN0001234"
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Internal Remarks</label>
                  <input
                    type="text"
                    value={internalRemarks}
                    onChange={(e) => setInternalRemarks(e.target.value)}
                    placeholder="Internal Remarks"
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-slate-100 text-slate-700 text-xs font-semibold rounded-lg hover:bg-slate-200 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={recordPaymentMutation.isPending}
                className="px-5 py-2 bg-[#0f172a] text-white text-xs font-bold rounded-lg hover:bg-black active:scale-95 shadow-sm transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                {recordPaymentMutation.isPending ? 'Processing...' : 'Record & Request'}
              </button>
          </div>
        </form>
      </div>
    </div>
  );
};
